import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { safe_get } from "../../report-visuals";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import "react-checkbox-tree/lib/react-checkbox-tree.css";
import { FilterHeader } from "../voter-list";
import { useStore } from "../../helpers";
import { RNCheckBoxTree } from "../../components";
import { useLocalStorage } from "react-use";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { VortexSpinner } from "../digital-repository";

export const Booths = observer((props: any) => {
    const { projectStore, userStore } = useStore();
    const { activeUser } = userStore;

    const [t] = useTranslation("user_management");

    const hierarchy = projectStore.getProjectProperty("hierarchy", []);

    const allBooths = hierarchy.flatMap((level) => level.children || []);

    const [userPartsandPerms] = useLocalStorage("userpartsandperms");

    const [checked, setChecked] = useState(safe_get(userPartsandPerms, "parts", []));

    const [command, setCommand] = useState("");
    const [isListening, setIsListening] = useState(false);

    const [pendingAssignment, setPendingAssignment] = useState<any>(null);

    const [commandError, setCommandError] = useState("");

    const queryClient = useQueryClient();

    /*
     * ---------------------------------------------------------
     * USER RESOLUTION
     * ---------------------------------------------------------
     */

    const findUserByName = (name: string) => {
        const normalizedName = name.trim().toLowerCase();

        const matches = userStore.userList.filter(
            (user) => user.name?.trim().toLowerCase() === normalizedName
        );

        console.log("🔥 USER SEARCH:", name);
        console.log("🔥 USER MATCHES:", matches);

        if (matches.length === 0) {
            return {
                success: false,
                reason: "USER_NOT_FOUND",
                matches: [],
            };
        }

        if (matches.length > 1) {
            return {
                success: false,
                reason: "MULTIPLE_USERS",
                matches,
            };
        }

        return {
            success: true,
            user: matches[0],
        };
    };

    /*
     * ---------------------------------------------------------
     * BOOTH RESOLUTION
     * ---------------------------------------------------------
     */

    const findBoothByNumber = (boothNumber: number) => {
        const boothId = String(boothNumber).padStart(3, "0");

        const booth = allBooths.find((booth) => booth.id === boothId);

        console.log(`🔥 SEARCHING BOOTH ${boothNumber}:`, booth);

        return booth;
    };

    /*
     * ---------------------------------------------------------
     * GET CURRENT USER BOOTH ASSIGNMENTS
     * ---------------------------------------------------------
     */

    const getCurrentBoothIds = (user: any) => {
        if (!Array.isArray(user?.parts)) {
            console.warn("⚠️ User parts is not an array:", user?.parts);

            return [];
        }

        return user.parts
            .map((part: any) => {
                if (typeof part === "string") {
                    return part;
                }

                if (typeof part === "number") {
                    return String(part);
                }

                return part?.id ?? part?.value ?? null;
            })
            .filter(Boolean)
            .map((id: string | number) => String(id).padStart(3, "0"));
    };

    /*
     * ---------------------------------------------------------
     * PREPARE ASSIGNMENT
     * ---------------------------------------------------------
     */

    const prepareBoothAssignment = (userName: string, boothNumbers: number[]) => {
        console.log("🔥 PREPARING ASSIGNMENT");

        console.log("User:", userName);

        console.log("Booths:", boothNumbers);

        const userResult = findUserByName(userName);

        if (!userResult.success) {
            return userResult;
        }

        const booths = boothNumbers.map((number) => findBoothByNumber(number));

        const missingBooths = boothNumbers.filter((_, index) => !booths[index]);

        if (missingBooths.length > 0) {
            return {
                success: false,
                reason: "BOOTH_NOT_FOUND",
                missingBooths,
            };
        }

        const boothIds = booths.map((booth) => booth.id);

        const currentBoothIds = getCurrentBoothIds(userResult.user);

        /*
         * ADD mode:
         * Preserve existing booths.
         */

        const updatedBooths = [...new Set([...currentBoothIds, ...boothIds])];

        const result = {
            success: true,
            user: userResult.user,
            boothIds,
            currentBoothIds,
            updatedBooths,
            booths,
        };

        console.log("🔥 FINAL ASSIGNMENT DATA:", result);

        return result;
    };

    /*
     * ---------------------------------------------------------
     * PARSE COMMAND
     *
     * Example:
     *
     * Assign booths 165, 166, 167 to Akash Shetty
     * ---------------------------------------------------------
     */

    const parseAssignmentCommand = (text: string) => {
        const normalizedText = text.trim().replace(/\s+/g, " ");

        const match = normalizedText.match(/^assign(?:\s+booths?)?\s+(.+?)\s+to\s+(?:user\s+)?(.+)$/i);

        if (!match) {
            return {
                success: false,
                reason: "INVALID_COMMAND",
            };
        }

        const boothPart = match[1];
        const userName = match[2].trim();

        /*
         * Extract booth numbers.
         *
         * "165, 166, 167"
         * becomes
         * [165, 166, 167]
         */

        const boothNumbers = boothPart.match(/\d+/g)?.map(Number) || [];

        if (boothNumbers.length === 0) {
            return {
                success: false,
                reason: "NO_BOOTHS",
            };
        }

        return {
            success: true,
            userName,
            boothNumbers,
        };
    };

    /*
     * ---------------------------------------------------------
     * PROCESS COMMAND
     * ---------------------------------------------------------
     */

    const processAssignmentCommand = (text: string) => {
        setCommandError("");

        console.log("🎤 COMMAND:", text);

        const parsed = parseAssignmentCommand(text);

        console.log("🧠 PARSED COMMAND:", parsed);

        if (!parsed.success) {
            setCommandError("Use: Assign booths 165, 166, 167 to Akash Shetty");

            return;
        }

        const assignment = prepareBoothAssignment(parsed.userName, parsed.boothNumbers);

        if (!assignment.success) {
            if (assignment.reason === "USER_NOT_FOUND") {
                setCommandError(`User "${parsed.userName}" was not found.`);

                return;
            }

            if (assignment.reason === "MULTIPLE_USERS") {
                const names = assignment.matches
                    .map((user: any) => `${user.name} (ID: ${user.id})`)
                    .join(", ");

                setCommandError(`Multiple users found: ${names}`);

                return;
            }

            if (assignment.reason === "BOOTH_NOT_FOUND") {
                setCommandError(`Booth(s) not found: ${assignment.missingBooths.join(", ")}`);

                return;
            }

            setCommandError("Unable to understand the assignment.");

            return;
        }

        /*
         * Don't call API yet.
         *
         * Store the assignment for confirmation.
         */

        setPendingAssignment(assignment);

        console.log("🟡 PENDING ASSIGNMENT:", assignment);
    };

    /*
     * ---------------------------------------------------------
     * AI / VOICE ASSIGNMENT API
     * ---------------------------------------------------------
     */

    const assignBoothsToUser = async (assignment: any) => {
        console.log("🚀 STARTING BOOTH ASSIGNMENT");

        const payload = {
            projectid: projectStore.activeProject.id,

            userid: assignment.user.id,

            hierarchy: assignment.updatedBooths,
        };

        console.log("🔥 FINAL API PAYLOAD:", payload);

        const res = await userStore.updateUserHierarchy(payload);

        console.log("🔥 API RESPONSE:", res);

        if (!res.ok) {
            throw new Error("Something went wrong while assigning booths");
        }

        return {
            success: true,
            user: assignment.user,
            boothIds: assignment.updatedBooths,
            response: res,
        };
    };

    /*
     * ---------------------------------------------------------
     * AI ASSIGN MUTATION
     * ---------------------------------------------------------
     */

    const { mutate: assignMutation, isLoading: isAssigning } = useMutation({
        mutationFn: assignBoothsToUser,

        onSuccess: async (result) => {
            await queryClient.invalidateQueries({
                queryKey: ["users"],
                exact: false,
            });

            await queryClient.invalidateQueries({
                queryKey: ["user_permissions", result.user.id],
                exact: false,
            });

            toast.success(`Booths assigned successfully to ${result.user.name}`);

            setPendingAssignment(null);
            setCommand("");
            setCommandError("");

            /*
             * If the selected user is the same
             * user whose Booths screen is open,
             * update the tree immediately.
             */

            if (String(activeUser?.id) === String(result.user.id)) {
                setChecked(result.boothIds);
            }
        },

        onError: () => {
            toast.error("Something went wrong while assigning booths");
        },
    });

    /*
     * ---------------------------------------------------------
     * CONFIRM AI ASSIGNMENT
     * ---------------------------------------------------------
     */

    const confirmAssignment = () => {
        if (!pendingAssignment) {
            return;
        }

        assignMutation(pendingAssignment);
    };

    /*
     * ---------------------------------------------------------
     * VOICE RECOGNITION
     * ---------------------------------------------------------
     */

    const startVoiceCommand = () => {
        const SpeechRecognition =
            (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

        if (!SpeechRecognition) {
            toast.error("Voice recognition is not supported in this browser.");

            return;
        }

        const recognition = new SpeechRecognition();

        recognition.lang = "en-IN";
        recognition.continuous = false;
        recognition.interimResults = false;

        recognition.onstart = () => {
            setIsListening(true);
            setCommandError("");
        };

        recognition.onresult = (event: any) => {
            const transcript = event.results[0][0].transcript;

            console.log("🎤 VOICE RESULT:", transcript);

            setCommand(transcript);

            processAssignmentCommand(transcript);
        };

        recognition.onerror = (event: any) => {
            console.error("🎤 VOICE ERROR:", event);

            setCommandError("Could not understand the voice command.");

            setIsListening(false);
        };

        recognition.onend = () => {
            setIsListening(false);
        };

        recognition.start();
    };

    /*
     * ---------------------------------------------------------
     * EXISTING MANUAL ASSIGNMENT
     * ---------------------------------------------------------
     */

    const update_assigned_booths_to_server = async () => {
        const res = await userStore.updateUserHierarchy({
            projectid: projectStore.activeProject["id"],

            userid: userStore.activeUser["id"],

            hierarchy: checked,
        });

        if (!res.ok) {
            throw new Error("Something went wrong");
        }

        return res;
    };

    const { mutate, isLoading } = useMutation({
        mutationFn: update_assigned_booths_to_server,

        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["users"],
                exact: false,
            });

            toast.success("Booths have been assigned successfully");

            userStore.setUserDetailsMode(false);
        },

        onError: () => {
            toast.error("Something went wrong");
        },
    });

    return (
        <div className="my-4 flex flex-col justify-between rounded-lg bg-white px-4 py-4">
            <VortexSpinner visible={isLoading || isAssigning} />

            <FilterHeader title={"BOOTH FILTER"} content={"Filter by Booth number"} />

            {/* =================================================
                AI ASSIGN SECTION
            ================================================= */}

            <div className="mb-5 rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                <div className="mb-3">
                    <h3 className="text-lg font-semibold text-gray-800">🤖 AI Booth Assignment</h3>

                    <p className="text-sm text-gray-500">
                        Say or type:
                        <span className="ml-1 font-medium text-indigo-700">
                            "Assign booths 165, 166, 167 to Akash Shetty"
                        </span>
                    </p>
                </div>

                <div className="flex gap-2">
                    <input
                        type="text"
                        value={command}
                        onChange={(e) => setCommand(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                processAssignmentCommand(command);
                            }
                        }}
                        placeholder="Assign booths 165, 166, 167 to Akash Shetty"
                        className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    />

                    <button
                        type="button"
                        onClick={() => processAssignmentCommand(command)}
                        disabled={!command.trim()}
                        className="rounded-lg bg-indigo-600 px-4 py-2 font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Assign
                    </button>

                    <button
                        type="button"
                        onClick={startVoiceCommand}
                        disabled={isListening}
                        className={`rounded-lg px-4 py-2 font-medium text-white ${
                            isListening ? "bg-red-500" : "bg-gray-800 hover:bg-gray-900"
                        }`}
                    >
                        {isListening ? "🎙️ Listening..." : "🎤 Voice"}
                    </button>
                </div>

                {commandError && (
                    <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
                        {commandError}
                    </div>
                )}

                {/* =================================================
                    CONFIRMATION
                ================================================= */}

                {pendingAssignment && (
                    <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                        <div className="mb-2 font-semibold text-gray-800">Confirm Booth Assignment</div>

                        <div className="space-y-1 text-sm text-gray-700">
                            <div>
                                <strong>User:</strong> {pendingAssignment.user.name}
                            </div>

                            <div>
                                <strong>User ID:</strong> {pendingAssignment.user.id}
                            </div>

                            <div>
                                <strong>New booths:</strong> {pendingAssignment.boothIds.join(", ")}
                            </div>

                            <div>
                                <strong>Total assigned after update:</strong>{" "}
                                {pendingAssignment.updatedBooths.length}
                            </div>
                        </div>

                        <div className="mt-4 flex gap-2">
                            <button
                                type="button"
                                onClick={confirmAssignment}
                                disabled={isAssigning}
                                className="rounded-lg bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700 disabled:opacity-50"
                            >
                                {isAssigning ? "Assigning..." : "✓ Confirm Assignment"}
                            </button>

                            <button
                                type="button"
                                onClick={() => setPendingAssignment(null)}
                                disabled={isAssigning}
                                className="rounded-lg bg-gray-200 px-4 py-2 font-medium text-gray-700 hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* =================================================
                EXISTING MANUAL BOOTH TREE
            ================================================= */}

            {checked.length === 0 && (
                <div className="text-md text-bold p-2 text-red-300">{t("assign_booths")}</div>
            )}

            <div className="flex flex-col">
                <RNCheckBoxTree
                    data={hierarchy}
                    checked_nodes={checked}
                    on_node_selection={(nodes) => {
                        setChecked(nodes);

                        console.log("🔥 SELECTED NODES:", nodes);
                    }}
                />

                <button
                    className="flex self-end rounded-md bg-indigo-700 px-4 py-2 text-white"
                    onClick={() => mutate()}
                    disabled={isLoading}
                >
                    {isLoading ? "Assigning..." : t("assign")}
                </button>
            </div>
        </div>
    );
});
